import { httpGet, httpPost, httpPostRaw } from "@eres/rest";
import appConfig from "./config";
import { LookupTransformer } from "@eres/ejari-api/IApi";
import { MapApiLookUpTransformer } from "@eres/ejari-api/Utils";
import { Lookup } from "@eres/ejari-api/Entity";
import {
	HTActionRequest,
	HTAttachment,
	HTCountRequest,
	HTCountResponse,
	HTDocLookup,
	HTRequest,
	HTRequestResponse,
	HTRequestType,
	HTSearchRequest,
	HTSearchResponse,
	HTValidatePropertyRequest,
	PossibleErrorResponse
} from "./entities";
import _ from "lodash";
import { LocaleUtil, StringUtil } from "@eres/core";
import { DataURIToBlob } from "@eres/ssoapi/api";
import { RVLookup } from "@10x/rentalvaluation/api/api";
import Strings from "@my-dld/assets/strings";
import { RVResponse } from "@10x/rentalvaluation/api/entities";

export function loadLookUp( name: string, transformer: LookupTransformer = MapApiLookUpTransformer ): Promise<Lookup[]> {
	return httpGet( `${ appConfig.handTakeOverAPI().endPoint() }api/Lookups/GetData?name=${ name }` )
	.then( validateResponseX ).then( ( lookups: any ) => {
		return lookups.map( transformer );
	} );
}

export function loadDocsLookUp( name: string, param1: string ): Promise<HTDocLookup[]> {
	return httpGet( `${ appConfig.handTakeOverAPI().endPoint() }api/Lookups/GetData?name=${ name }&param1=${ param1 }` )
	.then( validateResponseX ).then( ( lookups: any ) => {
		return _.sortBy( lookups, x => {
			let localValue = LocaleUtil.getLocalValue( x );
			if ( localValue ) localValue = localValue.trim();
			return localValue;
		} );
	} );
}

export function validateProperty( request: HTValidatePropertyRequest ): Promise<RVResponse<PossibleErrorResponse>> {
	return httpPost( `${ appConfig.handTakeOverAPI().endPoint() }api/ServiceValidation/ServiceValidations`, request );
}

export function getRequestDetailsPropertyId( propertyId: number, type: HTRequestType ): Promise<RVResponse<HTRequest>> {
	return httpGet( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/GetRequestByPropertyId?propertyId=${ propertyId }&requestTypeId=${ type }` );
}

export function uploadDoc( id: number, attachment: HTAttachment ): Promise<RVResponse<{ TemporaryRequestId: number, Url: string }>> {
	const file = DataURIToBlob( attachment.LocalData! );
	const data = new FormData();
	data.append( "document", file, attachment.PhysicalName );
	return httpPostRaw( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/UploadFile?docId=${ id }`, data );
}

export function isUploadUrl( url: string ): boolean {
	return ( url.startsWith( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/UploadFile` ) );
}

export function submitRequest( request: HTRequest ): Promise<RVResponse<HTRequestResponse>> {
	return httpPost( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/SubmitRequest`, request );
}

export function getRequestsCount( req: HTCountRequest ): Promise<RVResponse<HTCountResponse>> {
	const partId = req.participantId ? req.participantId : "";
	const tenantId = req.tenantId ? req.tenantId : "";
	return httpGet( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/GetRequestCount?participantId=${ partId }&tenantId=${ tenantId }` );
}

export function getRequests( req: HTSearchRequest ): Promise<RVResponse<HTSearchResponse>> {
	return httpPost( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/Search`, req );
}

export function getRequestDetails( reqNo: string, participantId: string, tenantId: string ): Promise<RVResponse<HTRequest>> {
	return httpGet( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/GetRequestByNumber?requestNumber=${ reqNo }&participantId=${ participantId }&tenantId=${ tenantId }` );
}

export function updateAction( request: HTActionRequest ): Promise<RVResponse<HTRequestResponse>> {
	return httpPost( `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/ActionOnRequest`, request );
}

export function getDownloadCertificateURL( reqNo: string, participantId: string, tenantId: string ): string {
	return `${ appConfig.handTakeOverAPI().endPoint() }api/PhPt/PrintHandoverTakeoverCertificate?requestNumber=${ reqNo }&participantId=${ participantId }&tenantId=${ tenantId }`;
}

export function validateResponseX<T>( response: RVResponse<T> ): Promise<T> {
	if ( response ) {
		if ( response.Code === "0" ) {
			return Promise.resolve( response.Response );
		} else {
			return Promise.reject( response.Message );
		}
	} else {
		return Promise.reject( "Invalid Response" );
	}
}

export function validateResponse<T extends PossibleErrorResponse>( response: RVResponse<T> ): Promise<T> {
	if ( response ) {
		if ( response.Code === "0" ) {
			const r = response.Response;
			if ( r ) {
				const errors: RVLookup[] = r.Error || [];
				const codes: string[] = [];
				if ( errors.length > 0 ) {
					const error = errors.map( error => {
						codes.push( error.ErrorEnumCode );
						return {
							en: error.ErrorDescEn,
							ar: error.ErrorDescAr,
						};
					} ).map( value => LocaleUtil.getLocalValue( value ) ).join( "\n" );
					const ex = {
						message: error,
						codes: codes,
						type: "ValidationError",
					};
					return Promise.reject( ex );
				}
			}
			return Promise.resolve( response.Response );
		} else {
			let reason = response.Message;
			if ( reason ) {
				return Promise.reject( reason );
			} else {
				let errors = response.Errors;
				if ( errors && errors.length > 0 ) {
					reason = "";
					errors.reduce( ( previousValue, currentValue ) => {
						if ( currentValue.Message ) {
							reason = reason + "\n" + LocaleUtil.getLocalValue( {
								en: currentValue.Message.En,
								ar: currentValue.Message.Ar
							} );
						}
						return reason;
					}, reason );
					if ( !StringUtil.isEmptyStringOrNull( response.ExceptionId ) )
						reason = reason + "\n" + response.ExceptionId;
					return Promise.reject( reason );
				}
			}
			return Promise.reject( Strings.system_error );
		}
	} else {
		return Promise.reject( "Invalid Response" );
	}
}
